import type { GroupInfo } from "../../../shared/api-contract";
import type { ConfiguratorOption, OptionKind } from "../../../features/vault/configurator";
// Types only: the engine module draws in the browser-side registry, and
// `verify:configurator` runs this file under node against a fake engine.
import type { BasicEngine } from "../basic/applyEngine";
import { optionMark } from "./optionMark";

/**
 * Putting a configurator choice on the model INSIDE the studio.
 *
 * The shared link calls `applyOption` (raw RPCs). The studio goes through the
 * Basic engine instead, so trying a choice is one Undo step like every other
 * material change — and the engine's `putMaterial` already makes the decisions
 * `applyOption` makes (reclassify first; the real diamond shader under a gem
 * file). `optionMark` translates an option into the engine's vocabulary.
 */

/** What putting a choice on the model did. */
export interface PreviewResult {
  /**
   * `"dmat"` when a .pmat turned out to DECLARE a diamond (worth recording on
   * the option, so the shared link routes it right the first time).
   */
  format?: "dmat";
  /**
   * Nothing was applied: the choice is a metal COLOUR (a configurator saved
   * before the material library) and the slot is a stone. A colour is a
   * metal's; it used to put white gold on the stones first, which is one of
   * the ways "Gem 01" became metal.
   */
  skipped?: "colourOnStone";
}

/**
 * Show `option` on `group`, undoably. `slotKind` is the slot's STABLE kind
 * (`slotKindOf`), not what the part is wearing right now.
 */
export async function previewOption(
  engine: BasicEngine,
  group: GroupInfo,
  option: ConfiguratorOption,
  slotKind: OptionKind,
): Promise<PreviewResult> {
  const label = `Choice: ${option.name}`;
  const mark = optionMark(option);
  if (mark) {
    await engine.applyLibraryMaterial([group.id], mark, label, "configurator");
    const learned = mark.url && !mark.dmat && engine.presetFor(group.id)?.dmat === true;
    return learned ? { format: "dmat" } : {};
  }
  if (option.metalColor) {
    if (slotKind !== "metal") return { skipped: "colourOnStone" };
    // A metal slot that is showing as a stone right now gets its metal back
    // first — that is what the slot is.
    if (group.kind !== "metal") {
      await engine.applyLibraryMaterial([group.id], { presetId: "whiteGold", finishId: "polished", category: "metal" }, label, "configurator");
    }
    await engine.setValues([{ key: "metal.color", value: option.metalColor }], label, { gem: "all", metal: group.id }, "configurator");
  }
  return {};
}
